import { useMemo } from 'react';

import {
  useListProfileDocuments,
  useProfileSummary,
} from '../../api/tenant-profile-documents-api';
import { useTemplateOutline } from '../../api/tenant-profile-template-api';
import type {
  ActiveTemplateOutline,
  ProfileSummaryDto,
  TenantProfileDocumentDto,
} from '../../api/types';

import {
  deriveCategoryStatus,
  type DerivedCategory,
} from './deriveCategoryStatus';

export interface OrganizationProfileData {
  outline: ActiveTemplateOutline | undefined;
  docs: TenantProfileDocumentDto[];
  summary: ProfileSummaryDto | undefined;
  /** Derived status per category, keyed by category id. */
  derivedById: Map<string, DerivedCategory>;
  /** Categories that have at least one saved value (completion metric). */
  completedCount: number;
  categoryCount: number;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Orchestration hook: composes the template outline + documents + summary React
 * Query hooks (documents/summary already poll while work is in flight) and
 * memoizes the derived per-category status map. The outline is lightweight
 * (category metadata + completion counts, no fields); each category's fields are
 * lazy-loaded by the panel via `useCategoryWithValues`.
 */
export function useOrganizationProfileData(): OrganizationProfileData {
  const outlineQuery = useTemplateOutline();
  const documentsQuery = useListProfileDocuments();
  const summaryQuery = useProfileSummary();

  const outline = outlineQuery.data;
  const docs = useMemo(() => documentsQuery.data ?? [], [documentsQuery.data]);

  const { derivedById, completedCount } = useMemo(() => {
    const map = new Map<string, DerivedCategory>();
    let completed = 0;
    for (const meta of outline?.categories ?? []) {
      const derived = deriveCategoryStatus(meta);
      map.set(meta.id, derived);
      if (derived.hasSavedValues) completed += 1;
    }
    return { derivedById: map, completedCount: completed };
  }, [outline]);

  return {
    outline,
    docs,
    summary: summaryQuery.data,
    derivedById,
    completedCount,
    categoryCount: outline?.categories.length ?? 0,
    isLoading: outlineQuery.isLoading,
    isError: outlineQuery.isError,
  };
}
